<template>
  <div :class="['search-container', { 'element--is-loading': isLoading }]">
    <div class="block__top">
      <div class="block__top-container">
        <div class="section-block__top">
          <h2 class="section-block__header">
            "{{ searchPhrase }}"
          </h2>
        </div>

        <nav class="search-tabs">
          <button
            v-for="tab of visibleSearchTabs"
            :key="tab.name"
            :class="{ 'search-tab--is-active': tab.isActive }"
            class="search-tab"
            @click="changeTab(tab)">
            {{ tabsTriggerDisplayName(tab) }}
          </button>
        </nav>
      </div>
    </div>

    <div class="block__bottom">
      <oie-accordion-tabs css-classes="accordion-tabs--search" :hide-buttons="true">
        <oie-accordion-tab
          name="items"
          :display-name="`${tStatic('items')} ${getCount(productListAmount)}`"
          :selected="isTabActive('items')">
          <div class="search-container__products-tab">
            <oie-product-list-search-page-wrapper
              v-if="relatedProducts"
              ref="productList"
              class="search-container__products"
              :product-ids="relatedProducts"
              event-id="product-list"
              :type-of-listing="typeOfListing"
              :enable-filters="clientModel.enableSearchSpecificationFacets">
              <template v-slot:filters>
                <oie-filters v-if="clientModel.enableSearchSpecificationFacets"></oie-filters>
              </template>
            </oie-product-list-search-page-wrapper>
          </div>
        </oie-accordion-tab>

        <oie-accordion-tab
          name="categories"
          :display-name="`${tStatic('categories')} ${getCount(entities.categories.totalCount)}`"
          :selected="isTabActive('categories')">
          <oie-the-search-category-list :category-list="entities.categories.data"></oie-the-search-category-list>
        </oie-accordion-tab>

        <oie-accordion-tab
          name="models"
          :display-name="`${tStatic('models')} ${getCount(entities.models.totalCount)}`"
          :selected="isTabActive('models')">
          <section class="section-block">
            <oie-layout-grid class="grid--four grid--less-gap">
              <a
                v-for="vehicle of entities.models.data"
                :key="`vehicle-item-${vehicle.code}`"
                :href="vehicle.url"
                class="navigational-item navigational-item--arrow navigational-item--light-blue navigational-item--model-item">
                <span class="navigational-item__smaller-text">{{ vehicle.name.split('/')[0] }}</span>
                <span v-html="renderHighlight(vehicle.nameHighlight, vehicle.name)"></span>
              </a>
            </oie-layout-grid>
          </section>
        </oie-accordion-tab>

        <oie-accordion-tab
          name="superuser"
          :display-name="`${tStatic('superUserSearchResults')} ${getCount(superUserResultCount)}`"
          :selected="isTabActive('superuser')">
          <oie-instant-search-superuser-section
            v-if="hasSuperUserResults"
            :items="entities.items.extraResults"
            :show-header="false"
            class="search-container__superuser"></oie-instant-search-superuser-section>
        </oie-accordion-tab>
      </oie-accordion-tabs>
    </div>
  </div>
</template>

<script>
import search from '@/mixins/search'
import { renderHighlight } from '@/core/search-service'
import utils from '@/core/utils'
import _ from 'lodash'
import { mapGetters, mapActions } from 'vuex'
import { state as filterState, storeInitialFacetOptions, selectedFilters, setFilterQueryParams, applyFilterQueryParams, getCachedInitialFilters, storeFilters, getUrlFilterQueryParams, allFilterParameters, checkLastFacetUsed, hasSelectedFilters } from '@/store/filters'
import { listTypes } from '@/core/analytics-variables'

export default {
  mixins: [search],
  props: {
    searchPhrase: {
      type: String,
      required: true
    }
  },
  data: () => ({
    relatedProducts: null,
    productListAmount: 0,
    useBackCache: true,
    isLoading: false
  }),
  computed: {
    ...mapGetters('ui', ['searchTabs', 'showFilter']),
    activeTab() {
      return utils.getQueryStringParam('activeTab')
    },
    selectedFilters() { return selectedFilters() },
    // Only superusers get these: the item's Business Central rows, the same section the instant search shows.
    superUserResultCount() {
      return (this.entities.items.extraResults || []).length
    },
    hasSuperUserResults() {
      return this.superUserResultCount > 0
    },
    // The superuser tab is in the list from the start, since the rows arrive after mount, but its
    // button only appears once there are rows to show.
    visibleSearchTabs() {
      return (this.searchTabs || []).filter(tab => tab.name !== 'superuser' || this.hasSuperUserResults)
    },
    typeOfListing() {
      return hasSelectedFilters() ? listTypes.SEARCH_LISTING_FILTERED : listTypes.SEARCH_LISTING
    }
  },
  watch: {
    'entities.items.data'(data) {
      this.relatedProducts = _.map(data, 'itemId')
    },
    'entities.items.totalCount'(data) {
      this.productListAmount = data
    },
    // A link to the superuser tab opened by someone who gets no BC rows would otherwise show an empty page.
    'entities.items.extraResults'(rows) {
      if (!(rows && rows.length) && this.isTabActive('superuser')) {
        this.changeTab({ name: 'items' })
      }
    }
  },
  async mounted() {
    if (this.searchPhrase) {
      this.isLoading = true
    }

    this.handleScroll()
    this.searchQuery = utils.getQueryStringParam('query')
    this.searchType = 'Search page'
    _.forEach(this.entities, (entity, name) => {
      this.setPageSizeOrDefault(name)
    })
    this.initSearchTabs(this.initSearchTabsData())
    this.searchModels()
    this.searchCategories()
    this.searchItems(getUrlFilterQueryParams())

    if(this.clientModel.enableSearchSpecificationFacets) {
      this.initializeFilters()
    }

    this.subscribe('product-list-page-wrapper.products-updated', (products) => {
      this.isLoading = false
    })
    this.subscribe(`accordion-tab-changed`, (tabName) => {
      const tabs = { 'tab-1': 'items', 'tab-2': 'models', 'tab-3': 'categories' }
      // eslint-disable-next-line
      var b = tabs[tabName]
      utils.setQueryStringParameter('activeTab', tabs[tabName])
    })
    // this.setActiveSearchTab(this.activeTab())
  },
  methods: {
    renderHighlight,
    ...mapActions('ui', ['setActiveSearchTab', 'initSearchTabs', 'toggleValue']),
    setPageSizeOrDefault(name) {
      const pageSizeParam = utils.getQueryStringParam(name)
      if (pageSizeParam) {
        this.entities[name].pageSize = pageSizeParam
      }
    },
    handleScroll() {
      const map = {
        items: '.tabs__accordion-section:nth-child(1) .product-shell',
        categories: '.tabs__accordion-section:nth-child(2) .navigational-item',
        models: '.tabs__accordion-section:nth-child(3) .navigational-item',
        superuser: '.tabs__accordion-section:nth-child(4) .instant-result__item'
      }
      utils.rememberScroll(document.location.href, map[this.activeTab])
    },
    getCount(n) {
      return !n ? '' : `(${n > 200 ? '200+' : n})`
    },
    changeTab(tab) {
      utils.setQueryStringParameter('activeTab', tab.name)
      this.setActiveSearchTab(tab)
    },
    initSearchTabsData() {
      return [
        {
          name: 'items',
          displayName: `${this.tStatic('items')} ${this.getCount(this.productListAmount)}`,
          isActive: this.activeTab === 'items'
        },
        {
          name: 'models',
          displayName: `${this.tStatic('models')} ${this.getCount(this.entities.models.totalCount)}`,
          isActive: this.activeTab === 'models'
        },
        {
          name: 'categories',
          displayName: `${this.tStatic('categories')} ${this.getCount(this.entities.categories.totalCount)}`,
          isActive: this.activeTab === 'categories'
        },
        {
          name: 'superuser',
          displayName: `${this.tStatic('superUserSearchResults')} ${this.getCount(this.superUserResultCount)}`,
          isActive: this.activeTab === 'superuser'
        }
      ]
    },
    isTabActive(name) {
      const tab = this.searchTabs && this.searchTabs.filter((tab) => tab.name === name)[0]
      return tab ? tab.isActive : false
    },
    tabsTriggerDisplayName(tab) {
      let displayName

      if (tab.name === 'items') {
        displayName = `${this.tStatic('items')} ${this.getCount(this.productListAmount)}`
      }

      if (tab.name === 'categories') {
        displayName = `${this.tStatic('categories')} ${this.getCount(this.entities.categories.totalCount)}`
      }

      if (tab.name === 'models') {
        displayName = `${this.tStatic('models')} ${this.getCount(this.entities.models.totalCount)}`
      }

      if (tab.name === 'superuser') {
        displayName = `${this.tStatic('superUserSearchResults')} ${this.getCount(this.superUserResultCount)}`
      }

      return displayName
    },
    async setInitialFilters() {
      const cachedInitialFilters = getCachedInitialFilters(this.query)
      if(cachedInitialFilters) {
        storeInitialFacetOptions(cachedInitialFilters)
        storeFilters(cachedInitialFilters)
      } else {
        const filters = await this.getFilters()
        storeInitialFacetOptions(filters)
        storeFilters(filters)
        sessionStorage.setItem('initialFilters', JSON.stringify({ key: this.query, data: filters }))
      }
      applyFilterQueryParams()
    },
    async initializeFilters() {
      await this.setInitialFilters()
      checkLastFacetUsed()
      // Has pre-existing filters when coming to page? -> refetch for updated filters
      if(hasSelectedFilters()) {
        const filters = await this.getFilters(allFilterParameters())
        storeFilters(filters)
      }

      this.$watch('selectedFilters', (newVal, oldVal) => {
        if(!_.isEqual(newVal, oldVal)) {
          setFilterQueryParams()
          this.updateItemsAndFilters()
          // Todo: Refactor this scroll thing to more generic solution, maybe utility function or smth.
          const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
          if(reduceMotion && reduceMotion.true) {
            window.scrollTo({ top: 200, behavior: 'instant' })
          } else {
            window.scrollTo({ top: 200, behavior: 'smooth' })
          }
        }
      }, { deep: true })

      filterState.filtersInitialized = true
    }
  }
}
</script>

<style lang="scss">
.search-container .section-block__top {
  padding-bottom: 0;
  margin-bottom: measure(quarter);

  @include media(small) {
    margin-bottom: measure(half);
  }
}

// White cards, as the categories and models tabs show their results.
.search-container__superuser .instant-result__list {
  max-width: 40rem;
  padding-top: 0;
}

.search-container__superuser .instant-result__item {
  background-color: color(background-white);
  border: 1px solid color(border-gray);
}

.search-container__superuser .instant-result__link {
  padding-right: measure(half);
}

.search-container__products-tab {
  display: flex;
}

.search-container__products {
  flex-grow: 1;
}

.search-tabs {
  display: flex;
  flex-direction: column;
  margin-bottom: measure(half);

  @include media(small) {
    flex-direction: row;
  }
}

.search-tab {
  @include body-semi-bold;
  width: 12.5rem;
  height: 3.5rem;
  margin-right: measure(eighth);
  padding: measure(quarter);
  background-color: color(background-white);
  border: 1px solid color(border-gray);
  margin-right: 0;
  margin-bottom: measure(eighth);
  flex-grow: 1;
  width: auto;
  cursor: pointer;

  @include media(small) {
    flex-grow: 0;
    margin-bottom: 0;
    margin-right: measure(eighth);
    width: 12.5rem;
  }
}

.search-tab--is-active {
  background-color: color(background-blue-2);
  color: color(text-inverted);
}

.accordion-tabs--search .tabs__accordion-content {
  padding-top: 0;
}

.navigational-item--model-item em {
    background-color: #44B1FA;
    font-style: normal;
    color: white;
    padding: 0rem .1rem 0rem .05rem;
}

.navigational-item--model-item em.instant-result__complete_match {
    background-color: #006db6;
    padding: .15rem .4rem .15rem .4rem;
    border-radius: 2px;
}
</style>
